<script>
import Tooltip from '@components/tooltip.vue'
import CopyToClipboardButton from '@components/copy-to-clipboard-button.vue'
export default {
  components: {
    Tooltip,
    CopyToClipboardButton,
  },
  inheritAttrs: false,
  props: {
    mask: {
      type: [String, Function],
      default: '',
    },
    label: {
      type: String,
      required: true,
    },
    subLabel: {
      type: String,
      default: '',
    },
    tooltipText: {
      type: String,
      default: '',
    },
    tooltipAtTop: {
      type: Boolean,
    },
    name: {
      type: String,
      default: '',
    },
    value: {
      type: [String, Number, Object, Array],
      default: '',
    },
    valueArray: {
      type: Array,
      default: () => [],
    },
    characterLimitOptions: {
      type: Object,
      default: () => ({
        showOnlyError: false,
      }),
    },
    editMode: {
      type: Boolean,
    },
    copyable: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
    },
    formatter: {
      type: Function,
      default: null,
    },
    editSelectValues: {
      type: [Object, Array],
      default: null,
    },
    showInListFormat: {
      type: Boolean,
    },
    multiSelect: {
      type: Boolean,
    },
    textarea: {
      type: Boolean,
    },
    error: {
      type: String,
      default: '',
    },
    rules: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    options: {
      type: Object,
      default: () => ({
        hideAsterisk: false,
        hideLabel: false,
      }),
    },
  },
  data() {
    return { modelValue: this.value, formItemId: crypto.randomUUID() }
  },
  computed: {
    formattedValue() {
      if (this.formatter) {
        return this.formatter(this.value)
      }
      return this.value
    },
    isRequired() {
      return this.rules.includes('required')
    },
    characterInfo() {
      try {
        const rules = this.rules.split('|').filter((x) => x.includes('max:'))
        const limit = rules[0].split(':')[1]
        const isOverLimit = this.modelValue.length > limit

        if (!isOverLimit && this.characterLimitOptions.showOnlyError) {
          return false
        }

        return {
          charLimit: limit,
          currentCharCount: this.modelValue.length,
          isOverLimit: this.modelValue.length > limit,
        }
      } catch (err) {
        return false
      }
    },
  },
  watch: {
    value: {
      handler: function (newVal) {
        this.modelValue = newVal
      },
    },
  },
  methods: {
    onInput(inputEvent) {
      let value = inputEvent
      if (inputEvent instanceof Event) {
        value = inputEvent.target?.value || null
      }
      this.$emit('update:value', value)
      this.$emit('update', value)
    },
  },
}
</script>

<template>
  <div :class="$style.container">
    <div v-if="!options.hideLabel" :class="$style.labelContainer">
      <div>
        <p :class="$style.label" class="p1">
          {{ label }}:
          <span
            v-if="isRequired && editMode && !options.hideAsterisk"
            data-required-asterisk
            :class="$style.requiredAsterisk">
            *
          </span>
        </p>
      </div>
      <CopyToClipboardButton
        v-if="copyable && value"
        :class="$style.copyToClipboardButton"
        theme="square"
        :text-to-copy="value" />
      <div>
        <Tooltip v-if="tooltipText" :target="`${label}${formItemId}`" placement="top" theme="blue">
          <div v-html="tooltipText" />
        </Tooltip>
      </div>
    </div>
    <p v-if="subLabel && editMode" :class="$style.formItemSubLabel">{{ subLabel }}</p>

    <template v-if="!editMode">
      <div :class="$style.valueContainer">
        <h4 v-if="showInListFormat && value instanceof Array && !value.length" :class="$style.value">{{ 'N/A' }}</h4>
        <ul v-else-if="showInListFormat" :class="$style.valuesList">
          <template v-if="value instanceof Array">
            <li v-for="item in value" :key="item">
              {{ item }}
            </li>
          </template>
          <li v-for="item in valueArray" v-else :key="item">
            {{ item }}
          </li>
        </ul>
        <h4 v-else-if="value" :class="$style.value">{{ formattedValue || 'N/A' }}</h4>
        <h4 v-else :class="$style.value">{{ 'N/A' }}</h4>
        <slot name="afterValue"></slot>
      </div>
    </template>

    <template v-if="editMode">
      <ValidationProvider v-slot="{ errors, changed }" :debounce="300" :name="label" :rules="rules">
        <BaseMultiSelect
          v-if="multiSelect"
          v-model="modelValue"
          :options="editSelectValues"
          :placeholder="placeholder"
          @input="onInput"></BaseMultiSelect>
        <BaseSelect
          v-else-if="editSelectValues"
          v-model="modelValue"
          :options="editSelectValues"
          option-text="label"
          option-value="value"
          :disabled="disabled"
          :changed="changed"
          :empty-text="placeholder"
          @change="onInput"></BaseSelect>
        <p v-if="errors[0] && (multiSelect || editSelectValues)" :class="$style.errorMessage" class="tag-text">
          {{ errors[0] }}
        </p>

        <template v-if="!multiSelect && !editSelectValues">
          <BaseInputText
            v-model="modelValue"
            :disabled="disabled"
            :mask="mask"
            :name="name || label"
            :error="errors[0]"
            :placeholder="placeholder"
            :textarea="textarea"
            :changed="changed"
            @input="onInput" />
        </template>
        <p
          v-if="characterInfo"
          :class="[$style.charCounter, characterInfo.isOverLimit ? $style.errorMessage : '']"
          class="tag-text">
          {{ characterInfo.currentCharCount }} of {{ characterInfo.charLimit }} characters
        </p>
      </ValidationProvider>
    </template>
  </div>
</template>

<style lang="scss" module>
@import '@design';

.container {
  overflow-y: initial;

  div[class*='errorContainer'] {
    position: relative;
  }
}

.valueContainer {
  display: flex;
  align-items: center;
  padding-bottom: 8px;
  word-break: break-all;

  h4 {
    margin: 0;
  }
}

.labelContainer {
  position: relative;
  display: flex;
  align-items: center;

  div[data-tooltip-icon] {
    position: initial;
    display: flex;
    align-items: center;
    margin-left: 8px;
  }

  .label {
    margin-top: 10px;
    margin-bottom: 10px;
    color: $dark-gray;
  }
}

.value {
  font-weight: 700;
  color: $primary-blue;
  overflow-wrap: break-word;
}

.valuesList {
  padding-left: 0;
  font-weight: 700;
  list-style: none;

  li {
    margin-bottom: 5px;
    font-size: 16px;
    font-weight: 700;
    color: $primary-blue;
  }
}

.requiredAsterisk {
  font-weight: 700;
  color: $secondary-red;
}

.formItemSubLabel {
  margin: -8px 0 6px;
  color: $secondary-light-blue;
}

.errorMessage {
  @extend %error-message;

  text-align: right;
}

.copyToClipboardButton {
  margin-top: -2px;
  margin-left: 8px;
}

.charCounter {
  margin-top: 8px;
  margin-bottom: 0;
  font-size: 13px;
  color: $dark-gray;
  text-align: right;
}
</style>
